import { Check, FlaskConical, XCircle } from "lucide-react";
import type { RunResult } from "@/lib/runtimes/types";
import { cn } from "@/lib/cn";

interface Props {
  state:
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "done"; result: RunResult };
}

export function ResultsPanel({ state }: Props) {
  return (
    <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden h-full flex flex-col">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-tight flex items-center gap-2">
          <FlaskConical className="h-3.5 w-3.5 text-primary" />
          Resultados
        </h2>
        {state.kind === "done" && (
          <ScoreBadge
            score={state.result.score}
            passed={state.result.passedCount}
            total={state.result.totalCount}
          />
        )}
      </div>
      <div className="flex-1 overflow-auto p-4">
        {state.kind === "idle" && (
          <div className="text-center py-12 text-sm text-muted-foreground">
            Hacé click en <strong>Comenzar Ejercicio</strong> para correr los
            tests.
          </div>
        )}
        {state.kind === "running" && (
          <div className="text-center py-12 text-sm text-warning animate-pulse">
            Corriendo tests…
          </div>
        )}
        {state.kind === "done" && (
          <DoneResults result={state.result} />
        )}
      </div>
    </div>
  );
}

function DoneResults({ result }: { result: RunResult }) {
  if (result.fatalError) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-3 text-sm text-destructive">
        <div className="font-semibold mb-1">Error</div>
        <code className="text-xs whitespace-pre-wrap font-mono">
          {result.fatalError}
        </code>
      </div>
    );
  }
  return (
    <ul className="space-y-1.5">
      {result.results.map((r, i) => (
        <li
          key={i}
          className={cn(
            "rounded-md border px-3 py-2",
            r.passed
              ? "border-success/30 bg-success/5"
              : "border-destructive/30 bg-destructive/5"
          )}
        >
          <div className="flex items-start gap-2">
            {r.passed ? (
              <Check className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
            )}
            <span
              className={cn(
                "text-sm",
                r.passed ? "text-success" : "text-destructive"
              )}
            >
              {r.name}
            </span>
          </div>
          {r.error && (
            <div className="text-[11px] text-muted-foreground font-mono mt-1.5 ml-6 break-all">
              └─ {r.error}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function ScoreBadge({
  score,
  passed,
  total,
}: {
  score: number;
  passed: number;
  total: number;
}) {
  const variant =
    score === 100 ? "success" : score >= 50 ? "warning" : "danger";
  const map = {
    success: "bg-success/15 text-success border-success/30",
    warning: "bg-warning/15 text-warning border-warning/30",
    danger: "bg-destructive/15 text-destructive border-destructive/30",
  } as const;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold border",
        map[variant]
      )}
    >
      {passed}/{total} · {score}%
    </span>
  );
}
